import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    TextField,
    Tooltip,
    Stack,
    LinearProgress,
    Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useSnackbar } from 'notistack';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef, MRT_SortingState,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_VisibilityState,
} from 'material-react-table';
import axios from 'axios';
import { accountingService } from '../api/accounting';
import { Accounting, type AccountingAttachment, type AccountingKind } from '../types/entities/accounting.entity';
import { AccountingCreateOrUpdateRequest } from '../types/requests/accounting.requests';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { isValid, parse } from 'date-fns';
import { APP_DATE_FORMAT, formatAppDate, formatAppDateTime } from '../utils/dateFormats';
import { MarkerHighlightTextField, MarkerTableText } from '../utils/textMarkerHighlight';
import { AccountingPdfJsPreview } from './AccountingPdfJsPreview';
import { TxtSplitEditor } from './txt-editor';

const ACCOUNTING_SHOW_HTML_SOURCE_KEY = 'accounting.showHtmlSource';

const STANDARD_EXCHANGE_CURRENCIES = ['CHF', 'USD', 'EUR'] as const;

const ACCOUNTING_AUTHOR_PRESETS = ['Christine Hofstetter', 'Peter Morávek'] as const;

const LEGACY_ACCOUNTING_AUTHOR = 'Christine Hofstetter';

const DEFAULT_NEW_ACCOUNTING_AUTHOR = 'Peter Morávek';

const accountingCurrencyFieldsShellSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    columnGap: 1.5,
    rowGap: 1,
    p: 1.75,
    borderRadius: '10px',
    background:
        'linear-gradient(125deg, rgba(30, 27, 75, 0.72) 0%, rgba(49, 46, 129, 0.68) 48%, rgba(59, 20, 92, 0.74) 100%)',
    outline: '1px solid rgba(34, 211, 238, 0.2)',
    outlineOffset: '1px',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05), inset 0 -1px 0 rgba(0, 0, 0, 0.28)',
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        color: 'rgba(255, 255, 255, 0.88)',
    },
    '& .MuiOutlinedInput-root': {
        bgcolor: 'rgba(12, 10, 28, 0.55)',
        color: 'rgba(255, 255, 255, 0.92)',
        '& fieldset': {
            borderColor: 'rgba(34, 211, 238, 0.22)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(34, 211, 238, 0.4)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(34, 211, 238, 0.58)',
            borderWidth: 2,
        },
        '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
            color: 'rgba(255, 255, 255, 0.72)',
        },
    },
    '& .MuiFormHelperText-root': {
        color: 'rgba(255, 255, 255, 0.65)',
    },
} as const;

const REFERENCE_YEAR_MIN = 1990;
const REFERENCE_YEAR_MAX = 2100;

const referenceYearToDate = (year: string): Date | null => {
    if (!/^\d{4}$/.test(year.trim())) {
        return null;
    }
    const y = Number(year);
    if (y < REFERENCE_YEAR_MIN || y > REFERENCE_YEAR_MAX) {
        return null;
    }
    return new Date(y, 0, 1);
};

type AccountingFormState = {
    kind: AccountingKind;
    author: string;
    date: Date | null;
    reference_year: string;
    doc: string;
    description: string;
    account_debit: string;
    account_debit_description: string;
    account_credit: string;
    account_credit_description: string;
    amount_currency: string;
    exchange_currency: string;
    exchange_rate: string;
    amount: string;
    attachments: AccountingAttachment[];
};

/** Parse API `YYYY-MM-DD` as local calendar date (avoids UTC timezone shifting the day). */
const parseAccountingDateString = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!match) {
        return null;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const PASTE_DATE_INPUT_FORMATS = ['dd.MM.yyyy', 'd.M.yyyy', 'dd/MM/yyyy', 'd/M/yyyy', 'yyyy-MM-dd'] as const;

/** Typed or pasted date in common EU / ISO shapes (e.g. `15.08.2025`). */
const parseFlexibleAccountingDateInput = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    const iso = parseAccountingDateString(trimmed);
    if (iso) {
        return iso;
    }
    for (const fmt of PASTE_DATE_INPUT_FORMATS) {
        const parsed = parse(trimmed, fmt, new Date());
        if (isValid(parsed)) {
            return parsed;
        }
    }
    return null;
};

const formatAccountingDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const accountingRowToFormState = (row: Accounting): AccountingFormState => ({
    kind: row.kind ?? 'booking',
    author: row.author?.trim() || LEGACY_ACCOUNTING_AUTHOR,
    date: row.date ? parseAccountingDateString(row.date) : null,
    reference_year:
        row.reference_year != null
            ? String(row.reference_year)
            : row.date
              ? String(parseAccountingDateString(row.date)?.getFullYear() ?? '')
              : '',
    doc: row.doc || '',
    description: row.description || '',
    account_debit: row.account_debit?.toString() || '',
    account_debit_description: row.account_debit_description || '',
    account_credit: row.account_credit?.toString() || '',
    account_credit_description: row.account_credit_description || '',
    amount_currency: row.amount_currency?.toString() ?? '',
    exchange_currency: row.exchange_currency || '',
    exchange_rate: row.exchange_rate?.toString() ?? '',
    amount: row.amount?.toString() ?? '',
    attachments: row.attachments ? [...row.attachments] : [],
});

const ensureDecimalPlaces = (value: string, decimalPlaces: number) => {
    if (!value) return value;
    const [integerPart, decimalPart = ''] = value.split('.');
    if (decimalPart.length === 0) {
        return `${integerPart}.${'0'.repeat(decimalPlaces)}`;
    }
    if (decimalPart.length < decimalPlaces) {
        return `${integerPart}.${decimalPart.padEnd(decimalPlaces, '0')}`;
    }
    return `${integerPart}.${decimalPart.slice(0, decimalPlaces)}`;
};

const buildPayloadFromFormState = (data: AccountingFormState): AccountingCreateOrUpdateRequest => {
    const author = data.author.trim();
    const refYearRaw = data.reference_year.trim();
    const reference_year = /^\d{4}$/.test(refYearRaw) ? Number(refYearRaw) : null;

    return {
    kind: data.kind ?? 'booking',
    author: author !== '' ? author : null,
    date: formatAccountingDate(data.date ?? new Date()),
    reference_year,
    doc: data.doc.trim(),
    description: data.description.trim(),
    account_debit: data.account_debit.trim() === '' ? 0 : Number(data.account_debit),
    account_debit_description: data.account_debit_description.trim(),
    account_credit: data.account_credit.trim() === '' ? 0 : Number(data.account_credit),
    account_credit_description: data.account_credit_description.trim(),
    amount_currency: Number(ensureDecimalPlaces(data.amount_currency, 2)),
    exchange_currency: data.exchange_currency.trim().toUpperCase(),
    exchange_rate: data.exchange_rate.trim() === '' ? 0 : Number(ensureDecimalPlaces(data.exchange_rate, 6)),
    amount: data.amount.trim() === '' ? 0 : Number(ensureDecimalPlaces(data.amount, 2)),
    attachments: [...data.attachments],
};
};

const payloadKeyForFormState = (data: AccountingFormState): string =>
    JSON.stringify(buildPayloadFromFormState(data));

export const AccountingTable: React.FC = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [referenceYearPickerOpen, setReferenceYearPickerOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selected, setSelected] = useState<Accounting | null>(null);
    const [formData, setFormData] = useState({
        kind: 'booking' as AccountingKind,
        author: DEFAULT_NEW_ACCOUNTING_AUTHOR,
        date: null as Date | null,
        reference_year: '',
        doc: '',
        description: '',
        account_debit: '',
        account_debit_description: '',
        account_credit: '',
        account_credit_description: '',
        amount_currency: '',
        exchange_currency: '',
        exchange_rate: '',
        amount: '',
        attachments: [] as AccountingAttachment[],
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const lastSavedPayloadKeyRef = useRef<string>('');
    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    /** Incremented when attachments change locally so in-flight save responses cannot overwrite UI with stale `attachments`. */
    const persistUiTokenRef = useRef(0);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const previewBlobUrlRef = useRef<string | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [horizontalScrollDirection, setHorizontalScrollDirection] = useState<-1 | 0 | 1>(0);
    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
    const [isClosingModal, setIsClosingModal] = useState(false);

    const exchangeCurrencyOptions = useMemo(() => {
        const v = formData.exchange_currency.trim().toUpperCase();
        if (v.length === 3 && !(STANDARD_EXCHANGE_CURRENCIES as readonly string[]).includes(v)) {
            return [...STANDARD_EXCHANGE_CURRENCIES, v];
        }
        return [...STANDARD_EXCHANGE_CURRENCIES];
    }, [formData.exchange_currency]);

    useEffect(() => {
        document.title = t('accounting.title');
    }, []);

    const LOCAL_STORAGE_FILTERS_KEY = 'accounting.table-filters.v1';
    const LEGACY_COLUMN_FILTERS_KEY = 'accounting_column_filters';
    const LOCAL_STORAGE_COLUMN_VISIBILITY_KEY = 'accounting_column_visibility.v2';

    const defaultColumnVisibility: MRT_VisibilityState = {
        kind: false,
        author: false,
        doc: false,
        description: false,
        account_debit: false,
        account_debit_description: false,
        account_credit: false,
        account_credit_description: false,
        exchange_rate: false,
        amount: false,
        created_at: false,
        updated_at: false,
    };
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [searchMode, setSearchMode] = useState<'contains' | 'fuzzy' | 'startsWith'>('contains');
    const [quickFilters, setQuickFilters] = useState({ lastYear: false, documentsOnly: false });
    const [isExportingZip, setIsExportingZip] = useState(false);
    const [filtersHydrated, setFiltersHydrated] = useState(false);
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'updated_at', desc: true }]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_COLUMN_VISIBILITY_KEY);
        if (saved) {
            try {
                return { ...defaultColumnVisibility, ...JSON.parse(saved) };
            } catch {
                return { ...defaultColumnVisibility };
            }
        }
        return { ...defaultColumnVisibility };
    });

    const {
        data: { accountings = [], meta } = {},
        isError,
        isRefetching,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: [
            'accounting-list',
            { columnFilters, globalFilter, searchMode, quickFilters, pagination, sorting },
        ],
        queryFn: async () => {
            const filters = buildListFilterQuery();
            const sort = buildSortParams(sorting);
            return accountingService.getAll(pagination.pageIndex + 1, pagination.pageSize, filters, sort);
        },
        placeholderData: keepPreviousData,
    });

    const createMutation = useMutation({
        mutationFn: (data: AccountingCreateOrUpdateRequest) => accountingService.create(data),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<AccountingCreateOrUpdateRequest> }) =>
            accountingService.update(id, data),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => accountingService.delete(id),
        onSuccess: () => {
            enqueueSnackbar(t('accounting.deleteSuccess'), { variant: 'success' });
            setIsDeleteModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['accounting-list'] });
        },
        onError: () => enqueueSnackbar(t('common.errorOccurred'), { variant: 'error' }),
    });

    const COLUMN_FILTER_SKIP = new Set(['attachments']);

    const buildFilterParams = (filters: MRT_ColumnFiltersState) => {
        return filters
            .filter(filter => {
                if (COLUMN_FILTER_SKIP.has(filter.id)) {
                    return false;
                }
                if (filter.value === undefined || filter.value === null) return false;
                if (Array.isArray(filter.value)) return filter.value.some(v => v !== undefined && v !== null);
                return true;
            })
            .map((filter) => {
                let operator = '$contains';
                let value: string | string[] = '';

                if (filter.id === 'kind' || filter.id === 'reference_year') {
                    operator = '$eq';
                }

                if (filter.id === 'created_at' || filter.id === 'updated_at' || filter.id === 'date') {
                    if (Array.isArray(filter.value)) {
                        const values = filter.value
                            .filter(v => v !== undefined && v !== null)
                            .map(v => {
                                if (filter.id === 'created_at' || filter.id === 'updated_at' || filter.id === 'date') {
                                    const date = v instanceof Date ? v : new Date(v);
                                    return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                                } else {
                                    return v.toString();
                                }
                            });

                        if (values.length === 2) {
                            operator = '$between';
                            value = values;
                        } else if (values.length === 1) {
                            if (filter.value[0] && !filter.value[1]) {
                                operator = '$gte';
                                value = values[0];
                            } else if (!filter.value[0] && filter.value[1]) {
                                operator = '$lte';
                                value = values[0];
                            } else {
                                return '';
                            }
                        } else {
                            return '';
                        }
                    } else {
                        return '';
                    }
                }
                else {
                    value = Array.isArray(filter.value)
                        ? filter.value.join(',')
                        : filter.value?.toString() || '';
                }

                if (!value) return '';

                if (operator === '$between' && Array.isArray(value) && value.length === 2) {
                    return [
                        `filters[${filter.id}][$betweenDate][0]=${encodeURIComponent(value[0])}`,
                        `filters[${filter.id}][$betweenDate][1]=${encodeURIComponent(value[1])}`
                    ].join('&');
                }

                const key = `filters[${filter.id}][${operator}]`;
                return `${key}=${encodeURIComponent(value.toString())}`;
            })
            .filter(Boolean)
            .join('&');
    };

    const buildGlobalFilterParams = (term: string, mode: 'contains' | 'fuzzy' | 'startsWith') => {
        const trimmed = term.trim();
        if (!trimmed) {
            return '';
        }
        return [
            `search_term=${encodeURIComponent(trimmed)}`,
            `search_mode=${encodeURIComponent(mode)}`,
        ].join('&');
    };

    const buildQuickFilterParams = (quick: { lastYear: boolean; documentsOnly: boolean }) => {
        const parts: string[] = [];
        if (quick.lastYear) {
            const year = new Date().getFullYear() - 1;
            parts.push(`filters[reference_year][$eq]=${year}`);
        }
        if (quick.documentsOnly) {
            parts.push('filters[kind][$eq]=document_only');
        }
        return parts.join('&');
    };

    const buildListFilterQuery = useCallback(() => {
        return [
            buildFilterParams(columnFilters),
            buildGlobalFilterParams(globalFilter, searchMode),
            buildQuickFilterParams(quickFilters),
        ]
            .filter(Boolean)
            .join('&');
    }, [columnFilters, globalFilter, quickFilters, searchMode]);

    const buildSortParams = (sortItems: MRT_SortingState) => {
        return sortItems
            .map((sort, index) => {
                const direction = sort.desc ? ":desc" : ":asc";
                return `sort[${index}]=${sort.id}${direction}`;
            })
            .join("&");
    }

    const handleOpenModal = (row?: Accounting, initialKind?: AccountingKind) => {
        if (row) {
            setSelected(row);
            const initialFormData = accountingRowToFormState(row);
            setFormData(initialFormData);
            lastSavedPayloadKeyRef.current = payloadKeyForFormState(initialFormData);
        } else {
            setSelected(null);
            const kind = initialKind ?? 'booking';
            const today = new Date();
            const initialFormData: AccountingFormState = {
                kind,
                author: DEFAULT_NEW_ACCOUNTING_AUTHOR,
                date: today,
                reference_year: String(today.getFullYear()),
                doc: '',
                description: '',
                account_debit: '',
                account_debit_description: '',
                account_credit: '',
                account_credit_description: '',
                amount_currency: '',
                exchange_currency: 'CHF',
                exchange_rate: '',
                amount: '',
                attachments: [],
            };
            setFormData(initialFormData);
            lastSavedPayloadKeyRef.current = payloadKeyForFormState(initialFormData);
        }
        setErrors({});
        setAutoSaveStatus('idle');
        setListPreviewAttachments(null);
        setIsPreviewFullscreenOpen(false);
        setIsModalOpen(true);
    };

    const handleDelete = (row: Accounting) => {
        setSelected(row);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (selected) {
            deleteMutation.mutate(selected.id);
        }
    };

    const handleGeneratePdf = async () => {
        const url =  await accountingService.generatePdf();
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleExportAttachmentsZip = async () => {
        setIsExportingZip(true);
        enqueueSnackbar(t('accounting.downloadAttachmentsPreparing'), { variant: 'info' });
        try {
            await accountingService.exportAttachmentsZip(buildListFilterQuery());
        } catch (err: unknown) {
            let message = t('accounting.downloadAttachmentsError');
            if (axios.isAxiosError(err)) {
                if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
                    message = t('accounting.downloadAttachmentsNetworkError');
                } else if (err.code === 'ECONNABORTED') {
                    message = t('accounting.downloadAttachmentsNetworkError');
                } else if (err.response?.status === 404) {
                    message = t('accounting.downloadAttachmentsEmpty');
                }
            } else if (err instanceof Error && err.message) {
                message = err.message;
            }
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsExportingZip(false);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            setFiltersHydrated(true);
            return;
        }

        try {
            const raw = window.localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw) as {
                    columnFilters?: MRT_ColumnFiltersState;
                    globalFilter?: string;
                    searchMode?: 'contains' | 'fuzzy' | 'startsWith';
                    quickFilters?: { lastYear?: boolean; documentsOnly?: boolean };
                };
                if (Array.isArray(parsed.columnFilters)) {
                    setColumnFilters(parsed.columnFilters);
                }
                if (typeof parsed.globalFilter === 'string') {
                    setGlobalFilter(parsed.globalFilter);
                }
                if (parsed.searchMode === 'contains' || parsed.searchMode === 'fuzzy' || parsed.searchMode === 'startsWith') {
                    setSearchMode(parsed.searchMode);
                }
                if (parsed.quickFilters && typeof parsed.quickFilters === 'object') {
                    setQuickFilters({
                        lastYear: !!parsed.quickFilters.lastYear,
                        documentsOnly: !!parsed.quickFilters.documentsOnly,
                    });
                }
            } else {
                const legacy = window.localStorage.getItem(LEGACY_COLUMN_FILTERS_KEY);
                if (legacy) {
                    setColumnFilters(JSON.parse(legacy) as MRT_ColumnFiltersState);
                }
            }
        } catch {
            // ignore malformed persisted filter state
        } finally {
            setFiltersHydrated(true);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !filtersHydrated) {
            return;
        }
        window.localStorage.setItem(
            LOCAL_STORAGE_FILTERS_KEY,
            JSON.stringify({ columnFilters, globalFilter, searchMode, quickFilters }),
        );
    }, [columnFilters, globalFilter, quickFilters, searchMode, filtersHydrated]);

    useEffect(() => {
        setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    }, [globalFilter, searchMode, quickFilters]);

    useEffect(() => {
        localStorage.setItem(
            LOCAL_STORAGE_COLUMN_VISIBILITY_KEY,
            JSON.stringify(columnVisibility)
        );
    }, [columnVisibility]);

    const isDocumentOnly = formData.kind === 'document_only';

    const validateForm = (options?: { silent?: boolean; data?: AccountingFormState }): boolean => {
        const silent = options?.silent ?? false;
        const data = options?.data ?? formData;
        const dataIsDocumentOnly = data.kind === 'document_only';
        const newErrors: Record<string, string> = {};
        if (!data.date) {
            newErrors.date = t('validation.required');
        }
        if (data.doc.length > 20) {
            newErrors.doc = t('validation.stringWithLength', { length: 20 });
        }
        if (!dataIsDocumentOnly) {
            if (data.account_debit && !/^\d{0,10}$/.test(data.account_debit)) {
                newErrors.account_debit = t('validation.numberWithLength', { length: 10 });
            }
            if (data.account_credit && !/^\d{0,10}$/.test(data.account_credit)) {
                newErrors.account_credit = t('validation.numberWithLength', { length: 10 });
            }
        }
        if (data.amount_currency) {
            const normalized = ensureDecimalPlaces(data.amount_currency, 2);
            if (!/^\d{0,8}(\.\d{2})?$/.test(normalized) || normalized.replace('.', '').length > 10) {
                newErrors.amount_currency = t('validation.decimalWithLength', { length: 2, example: '12.67' });
            }
        }
        if (data.exchange_currency?.trim()) {
            if (data.exchange_currency.trim().length !== 3) {
                newErrors.exchange_currency = t('validation.stringWithLength', { length: 3, example: '(e.g., USD)' });
            }
        }
        if (data.exchange_rate.trim()) {
            const normalized = ensureDecimalPlaces(data.exchange_rate, 6);
            if (!/^\d{0,4}(\.\d{6})?$/.test(normalized) || normalized.replace('.', '').length > 10) {
                newErrors.exchange_rate = t('validation.decimalWithLength', { length: 6, example: '12.345678' });
            }
        }
        if (data.amount.trim()) {
            const normalized = ensureDecimalPlaces(data.amount, 2);
            if (!/^\d{0,8}(\.\d{2})?$/.test(normalized) || normalized.replace('.', '').length > 10) {
                newErrors.amount = t('validation.decimalWithLength', { length: 2, example: '12345.67' });
            }
        }
        if (data.reference_year.trim() && !/^(19|20)\d{2}$/.test(data.reference_year.trim())) {
            newErrors.reference_year = t('validation.numberWithLength', { length: 4, example: '2025' });
        }
        if (!silent) {
            setErrors(newErrors);
        }
        return Object.keys(newErrors).length === 0;
    };

    const buildPayloadKey = (data: AccountingFormState = formData): string => payloadKeyForFormState(data);

    const closeModalImmediately = () => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        if (autoSaveResetTimerRef.current) {
            clearTimeout(autoSaveResetTimerRef.current);
            autoSaveResetTimerRef.current = null;
        }
        setIsModalOpen(false);
        setReferenceYearPickerOpen(false);
        setSelected(null);
        setFormData({
            kind: 'booking',
            author: DEFAULT_NEW_ACCOUNTING_AUTHOR,
            date: null,
            reference_year: '',
            doc: '',
            description: '',
            account_debit: '',
            account_debit_description: '',
            account_credit: '',
            account_credit_description: '',
            amount_currency: '',
            exchange_currency: '',
            exchange_rate: '',
            amount: '',
            attachments: [],
        });
        setErrors({});
        setAutoSaveStatus('idle');
        setIsClosingModal(false);
        setIsAttachmentDropOver(false);
        setPreviewPath(null);
        if (previewBlobUrlRef.current) {
            URL.revokeObjectURL(previewBlobUrlRef.current);
            previewBlobUrlRef.current = null;
        }
        setPreviewSrc(null);
        setPreviewMime('');
        setPreviewBlobLoading(false);
        setIsPreviewFullscreenOpen(false);
        setListPreviewAttachments(null);
        lastSavedPayloadKeyRef.current = '';
    };

    const persistAccounting = useCallback(
        async (
            data: AccountingFormState,
            opts: {
                isAutoSave: boolean;
                showSuccessToast: boolean;
                closeOnSuccess: boolean;
            },
        ): Promise<boolean> => {
            if (!validateForm(opts.isAutoSave ? { silent: true, data } : { data })) {
                if (opts.isAutoSave) {
                    setAutoSaveStatus('idle');
                } else {
                    enqueueSnackbar(
                        t('accounting.messages.validationFailed', { defaultValue: 'Please fix the form errors.' }),
                        { variant: 'warning' },
                    );
                }
                return false;
            }

            if (createMutation.isPending || updateMutation.isPending) {
                return false;
            }

            const payload = buildPayloadFromFormState(data);
            const keyAtSave = payloadKeyForFormState(data);
            if (opts.isAutoSave && keyAtSave === lastSavedPayloadKeyRef.current) {
                setAutoSaveStatus('idle');
                return true;
            }

            if (opts.isAutoSave) {
                setAutoSaveStatus('saving');
            }
            const wasUpdate = Boolean(selected?.id);
            const uiTokenAtStart = persistUiTokenRef.current;
            try {
                let saved: Accounting;
                if (selected?.id) {
                    saved = await updateMutation.mutateAsync({ id: selected.id, data: payload });
                } else {
                    saved = await createMutation.mutateAsync(payload);
                }
                if (persistUiTokenRef.current !== uiTokenAtStart) {
                    if (opts.isAutoSave) {
                        setAutoSaveStatus('idle');
                    }
                    return true;
                }

                lastSavedPayloadKeyRef.current = keyAtSave;
                setSelected(saved);

                const stillMatchesSavedPayload = payloadKeyForFormState(formDataRef.current) === keyAtSave;
                if (!opts.isAutoSave || stillMatchesSavedPayload) {
                    setFormData(accountingRowToFormState(saved));
                }

                await queryClient.invalidateQueries({ queryKey: ['accounting-list'] });
                if (opts.showSuccessToast && !opts.isAutoSave) {
                    enqueueSnackbar(wasUpdate ? t('accounting.updateSuccess') : t('accounting.createSuccess'), {
                        variant: 'success',
                    });
                }
                if (opts.isAutoSave) {
                    setAutoSaveStatus('saved');
                    if (autoSaveResetTimerRef.current) {
                        clearTimeout(autoSaveResetTimerRef.current);
                    }
                    autoSaveResetTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1400);
                }
                if (opts.closeOnSuccess) {
                    closeModalImmediately();
                }
                return true;
            } catch {
                if (opts.isAutoSave) {
                    setAutoSaveStatus('error');
                } else {
                    enqueueSnackbar(wasUpdate ? t('accounting.updateError') : t('accounting.createError'), {
                        variant: 'error',
                    });
                }
                return false;
            }
        },
        [createMutation, enqueueSnackbar, queryClient, selected?.id, t, updateMutation],
    );

    const triggerAutoSaveNow = useCallback(
        async (dataOverride?: AccountingFormState) => {
            if (!isModalOpen) {
                return;
            }
            const data = dataOverride ?? formData;
            if (!validateForm({ silent: true, data })) {
                return;
            }
            const key = payloadKeyForFormState(data);
            if (key === lastSavedPayloadKeyRef.current) {
                return;
            }
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
            await persistAccounting(data, { isAutoSave: true, showSuccessToast: false, closeOnSuccess: false });
        },
        [formData, isModalOpen, persistAccounting],
    );

    const requestCloseModal = async () => {
        if (isClosingModal) {
            return;
        }
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        const key = buildPayloadKey();
        if (key === lastSavedPayloadKeyRef.current) {
            closeModalImmediately();
            return;
        }
        if (!validateForm({ silent: true })) {
            enqueueSnackbar(
                t('accounting.messages.discardingInvalid', {
                    defaultValue: 'Form incomplete — closing without saving.',
                }),
                { variant: 'warning' },
            );
            closeModalImmediately();
            return;
        }
        setIsClosingModal(true);
        const ok = await persistAccounting(formData, { isAutoSave: true, showSuccessToast: false, closeOnSuccess: true });
        setIsClosingModal(false);
        if (!ok) {
            enqueueSnackbar(
                t('accounting.messages.autosaveBeforeCloseFailed', { defaultValue: 'Could not save before closing.' }),
                { variant: 'error' },
            );
        }
    };

    const handleSubmit = async () => {
        await persistAccounting(formData, { isAutoSave: false, showSuccessToast: true, closeOnSuccess: true });
    };

    const [isAttachmentDropOver, setIsAttachmentDropOver] = useState(false);
    const attachmentDragDepthRef = useRef(0);
    const [previewPath, setPreviewPath] = useState<string | null>(null);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const [previewMime, setPreviewMime] = useState<string>('');
    const [previewBlobLoading, setPreviewBlobLoading] = useState(false);
    const [isPreviewFullscreenOpen, setIsPreviewFullscreenOpen] = useState(false);
    /** When set, fullscreen prev/next + blob fetch use this list (table row); otherwise `formData.attachments`. */
    const [listPreviewAttachments, setListPreviewAttachments] = useState<AccountingAttachment[] | null>(null);

    const previewAttachments = listPreviewAttachments ?? formData.attachments;

    const openAttachmentPreview = useCallback((attachments: AccountingAttachment[], storedPath: string) => {
        if (!attachments.some((a) => a.stored_path === storedPath)) {
            return;
        }
        setListPreviewAttachments(attachments);
        setPreviewPath(storedPath);
        setIsPreviewFullscreenOpen(true);
    }, []);

    const closeAttachmentPreviewFullscreen = useCallback(() => {
        setIsPreviewFullscreenOpen(false);
        setListPreviewAttachments(null);
        if (!isModalOpen) {
            setPreviewPath(null);
        }
    }, [isModalOpen]);

    const largePreviewFileName = useMemo(
        () => previewAttachments.find((a) => a.stored_path === previewPath)?.original_name ?? '',
        [previewAttachments, previewPath],
    );

    const canFullscreenPreview =
        Boolean(previewSrc) &&
        (previewMime.includes('pdf') ||
            /\.pdf$/i.test(largePreviewFileName) ||
            previewMime.startsWith('image/'));

    const isPreviewPdf =
        Boolean(previewSrc) && (previewMime.includes('pdf') || /\.pdf$/i.test(largePreviewFileName));
    const isPreviewImage = Boolean(previewSrc) && previewMime.startsWith('image/');

    const largePreviewAttachmentIndex = useMemo(
        () => previewAttachments.findIndex((a) => a.stored_path === previewPath),
        [previewAttachments, previewPath],
    );

    const goLargePreviewPrev = useCallback(() => {
        const list = previewAttachments;
        if (list.length <= 1) {
            return;
        }
        const i = list.findIndex((a) => a.stored_path === previewPath);
        if (i <= 0) {
            return;
        }
        setPreviewPath(list[i - 1].stored_path);
    }, [previewAttachments, previewPath]);

    const goLargePreviewNext = useCallback(() => {
        const list = previewAttachments;
        if (list.length <= 1) {
            return;
        }
        const i = list.findIndex((a) => a.stored_path === previewPath);
        if (i < 0 || i >= list.length - 1) {
            return;
        }
        setPreviewPath(list[i + 1].stored_path);
    }, [previewAttachments, previewPath]);

    /** Stable while paths/order unchanged — avoids re-running preview sync when autosave replaces the array reference. */
    const attachmentsPathsKey = useMemo(
        () => formData.attachments.map((a) => a.stored_path).join('|'),
        [formData.attachments],
    );

    const attachmentsForSyncRef = useRef(formData.attachments);
    attachmentsForSyncRef.current = formData.attachments;

    const uploadAttachmentFileList = async (files: File[]) => {
        if (files.length === 0) {
            return;
        }
        persistUiTokenRef.current += 1;
        try {
            const newEntries: AccountingAttachment[] = [];
            for (const file of files) {
                const uploaded = await accountingService.uploadAttachment(file);
                newEntries.push({ stored_path: uploaded.stored_path, original_name: uploaded.original_name });
            }
            setFormData((prev) => ({ ...prev, attachments: [...prev.attachments, ...newEntries] }));
        } catch {
            enqueueSnackbar(
                t('accounting.messages.uploadFailed', { defaultValue: 'Upload failed.' }),
                { variant: 'error' },
            );
        }
    };

    const handleAttachmentsSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const list = event.target.files;
        if (!list?.length) {
            return;
        }
        await uploadAttachmentFileList(Array.from(list));
        event.target.value = '';
    };

    const isFileDragEvent = (e: React.DragEvent) => e.dataTransfer.types.includes('Files');

    const handleAttachmentDragEnter = (e: React.DragEvent) => {
        if (!isFileDragEvent(e)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        attachmentDragDepthRef.current += 1;
        if (attachmentDragDepthRef.current === 1) {
            setIsAttachmentDropOver(true);
        }
    };

    const handleAttachmentDragLeave = (e: React.DragEvent) => {
        if (!isFileDragEvent(e)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        attachmentDragDepthRef.current -= 1;
        if (attachmentDragDepthRef.current <= 0) {
            attachmentDragDepthRef.current = 0;
            setIsAttachmentDropOver(false);
        }
    };

    const handleAttachmentDragOver = (e: React.DragEvent) => {
        if (!isFileDragEvent(e)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
    };

    const handleAttachmentDrop = async (e: React.DragEvent) => {
        if (!isFileDragEvent(e)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        attachmentDragDepthRef.current = 0;
        setIsAttachmentDropOver(false);
        const files = Array.from(e.dataTransfer.files).filter((f) => f.size > 0);
        await uploadAttachmentFileList(files);
    };

    const removeAttachment = async (storedPath: string) => {
        persistUiTokenRef.current += 1;
        try {
            await accountingService.deleteStoredFile(storedPath);
        } catch {
            // ignore — still remove from form
        }
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((a) => a.stored_path !== storedPath),
        }));
    };

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        const list = attachmentsForSyncRef.current;
        if (list.length === 0) {
            setPreviewPath(null);
            return;
        }
        setPreviewPath((prev) => {
            if (prev && list.some((a) => a.stored_path === prev)) {
                return prev;
            }
            return list[0].stored_path;
        });
    }, [attachmentsPathsKey, isModalOpen]);

    useEffect(() => {
        if (!previewPath || !largePreviewFileName || !(isModalOpen || isPreviewFullscreenOpen)) {
            if (previewBlobUrlRef.current) {
                URL.revokeObjectURL(previewBlobUrlRef.current);
                previewBlobUrlRef.current = null;
            }
            setPreviewBlobLoading(false);
            setPreviewSrc(null);
            setPreviewMime('');
            return undefined;
        }
        const path = previewPath;
        const fileName = largePreviewFileName;
        setPreviewBlobLoading(true);
        setPreviewSrc(null);
        setPreviewMime('');
        let cancelled = false;
        void accountingService
            .fetchAttachmentBlob(path, fileName)
            .then((blob) => {
                if (cancelled) {
                    return;
                }
                if (previewBlobUrlRef.current) {
                    URL.revokeObjectURL(previewBlobUrlRef.current);
                }
                const url = URL.createObjectURL(blob);
                previewBlobUrlRef.current = url;
                setPreviewMime(blob.type || '');
                setPreviewSrc(url);
                setPreviewBlobLoading(false);
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                if (previewBlobUrlRef.current) {
                    URL.revokeObjectURL(previewBlobUrlRef.current);
                    previewBlobUrlRef.current = null;
                }
                setPreviewSrc(null);
                setPreviewMime('');
                setPreviewBlobLoading(false);
            });
        return () => {
            cancelled = true;
            if (previewBlobUrlRef.current) {
                URL.revokeObjectURL(previewBlobUrlRef.current);
                previewBlobUrlRef.current = null;
            }
        };
    }, [previewPath, largePreviewFileName, isModalOpen, isPreviewFullscreenOpen]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        const key = buildPayloadKey(formData);
        if (key === lastSavedPayloadKeyRef.current) {
            setAutoSaveStatus((prev) => (prev === 'pending' ? 'idle' : prev));
            return;
        }
        if (!validateForm({ silent: true, data: formData })) {
            setAutoSaveStatus((prev) => (prev === 'pending' ? 'idle' : prev));
            return;
        }
        if (createMutation.isPending || updateMutation.isPending) {
            return;
        }
        setAutoSaveStatus('pending');
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(() => {
            void triggerAutoSaveNow();
        }, 900);
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isModalOpen, selected?.id, createMutation.isPending, updateMutation.isPending, triggerAutoSaveNow]);

    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            if (autoSaveResetTimerRef.current) {
                clearTimeout(autoSaveResetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (horizontalScrollDirection === 0) {
            return;
        }

        const interval = window.setInterval(() => {
            const container = tableContainerRef.current;
            if (!container) {
                return;
            }

            container.scrollLeft += horizontalScrollDirection * 14;
        }, 16);

        return () => {
            window.clearInterval(interval);
        };
    }, [horizontalScrollDirection]);

    const columns = useMemo<MRT_ColumnDef<Accounting>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 88,
            },
            {
                accessorKey: 'reference_year',
                header: t('accounting.referenceYearShort', { defaultValue: 'Ref. year' }),
                filterVariant: 'text',
                size: 88,
                Cell: ({ cell }) => {
                    const v = cell.getValue<number | null>();
                    return v != null ? String(v) : '—';
                },
            },
            {
                accessorKey: 'date',
                header: t('accounting.date'),
                filterVariant: 'date-range',
                size: 112,
            },
            {
                accessorKey: 'amount_currency',
                header: t('accounting.amountShort', { defaultValue: 'Suma' }),
                size: 96,
                Cell: ({ cell }) => {
                    const v = cell.getValue<number | null>();
                    if (v == null || Number(v) === 0) {
                        return '—';
                    }
                    return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                },
            },
            {
                accessorKey: 'exchange_currency',
                header: t('accounting.currencyShort', { defaultValue: 'Mena' }),
                size: 72,
                Cell: ({ cell }) => cell.getValue()?.toString() || '—',
            },
            {
                id: 'attachments',
                accessorFn: (row) => row.attachments?.length ?? 0,
                header: t('accounting.attachments', { defaultValue: 'Attachments' }),
                enableColumnFilter: false,
                size: 220,
                Cell: ({ row }) => {
                    const attachments = row.original.attachments ?? [];
                    if (attachments.length === 0) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Stack spacing={0.25} sx={{ py: 0.25, maxWidth: 280 }}>
                            {attachments.map((a) => (
                                <Link
                                    key={a.stored_path}
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    title={t('accounting.previewFile', { defaultValue: 'Preview {{name}}', name: a.original_name })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openAttachmentPreview(attachments, a.stored_path);
                                    }}
                                    sx={{
                                        textAlign: 'left',
                                        display: 'block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {a.original_name}
                                </Link>
                            ))}
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'kind',
                header: t('accounting.kindDocument', { defaultValue: 'Type' }),
                size: 120,
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: t('accounting.kindBooking'), value: 'booking' },
                    { label: t('accounting.kindDocument'), value: 'document_only' },
                ],
                Cell: ({ cell }) => {
                    const kind = (cell.getValue() as AccountingKind) || 'booking';
                    return (
                        <Chip
                            size="small"
                            label={kind === 'document_only' ? t('accounting.kindDocument') : t('accounting.kindBooking')}
                            color={kind === 'document_only' ? 'info' : 'default'}
                            variant={kind === 'document_only' ? 'filled' : 'outlined'}
                        />
                    );
                },
            },
            {
                accessorKey: 'author',
                header: t('accounting.author', { defaultValue: 'Recorded by' }),
                filterVariant: 'text',
                size: 160,
                Cell: ({ cell }) => <MarkerTableText text={String(cell.getValue() ?? '')} />,
            },
            {
                accessorKey: 'doc',
                header: t('accounting.doc'),
                Cell: ({ cell }) => <MarkerTableText text={String(cell.getValue() ?? '')} />,
            },
            {
                accessorKey: 'description',
                header: t('accounting.description'),
                filterVariant: 'text',
                Cell: ({ cell }) => <MarkerTableText text={String(cell.getValue() ?? '')} />,
            },
            {
                accessorKey: 'account_debit',
                header: t('accounting.accountDebit'),
                Cell: ({ cell }) => cell.getValue()?.toString() ?? '',
            },
            {
                accessorKey: 'account_debit_description',
                header: t('accounting.accountDebitDescription'),
                Cell: ({ cell }) => <MarkerTableText text={String(cell.getValue() ?? '')} />,
            },
            {
                accessorKey: 'account_credit',
                header: t('accounting.accountCredit'),
                Cell: ({ cell }) => cell.getValue()?.toString() ?? '',
            },
            {
                accessorKey: 'account_credit_description',
                header: t('accounting.accountCreditDescription'),
                Cell: ({ cell }) => <MarkerTableText text={String(cell.getValue() ?? '')} />,
            },
            {
                accessorKey: 'exchange_rate',
                header: t('accounting.exchangeRate'),
                Cell: ({ cell }) => cell.getValue()?.toString() ?? '',
            },
            {
                accessorKey: 'amount',
                header: t('accounting.amount'),
                Cell: ({ cell }) => cell.getValue()?.toString() ?? '',
            },
            {
                id: 'created_at',
                accessorFn: (row) => new Date(row.created_at),
                header: t('accounting.created_at'),
                filterVariant: 'date-range',
                Cell: ({ cell }) => formatAppDate(cell.getValue<Date>()),
            },
            {
                id: 'updated_at',
                accessorFn: (row) => new Date(row.updated_at),
                header: t('accounting.updated_at', { defaultValue: 'Updated' }),
                filterVariant: 'date-range',
                size: 160,
                Cell: ({ cell }) => {
                    const d = cell.getValue<Date>();
                    return Number.isNaN(d.getTime()) ? '—' : formatAppDateTime(d);
                },
            },
        ],
        [t, openAttachmentPreview]
    );

    const table = useMaterialReactTable({
        columns,
        data: accountings,
        initialState: {
            showColumnFilters: true,
            columnPinning: { right: ['mrt-row-actions'] },
        },
        enableGlobalFilter: false,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        enableRowActions: true,
        positionActionsColumn: 'last',
        editDisplayMode: 'cell',
        enableCellActions: true,
        enableClickToCopy: 'context-menu',
        enableEditing: false,
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
                if (event.button !== 0) {
                    return;
                }
                const el = event.target as HTMLElement;
                if (el.closest('button, a, input, textarea, select, [role="checkbox"]')) {
                    return;
                }
                handleOpenModal(row.original);
            },
            sx: { cursor: 'pointer' },
        }),
        muiTableContainerProps: {
            ref: tableContainerRef,
            onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => {
                const container = tableContainerRef.current;
                if (!container) {
                    return;
                }

                const rect = container.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const edgeThreshold = 56;

                if (x >= rect.width - edgeThreshold) {
                    setHorizontalScrollDirection(1);
                    return;
                }

                if (x <= edgeThreshold) {
                    setHorizontalScrollDirection(-1);
                    return;
                }

                if (horizontalScrollDirection !== 0) {
                    setHorizontalScrollDirection(0);
                }
            },
            onMouseLeave: () => setHorizontalScrollDirection(0),
            sx: { overflowX: 'auto' },
        },
        muiToolbarAlertBannerProps: isError
            ? {
                color: 'error',
                children: t('common.errorFetchingData'),
            }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        renderTopToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Tooltip arrow title={t('common.refreshData')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="accounting-search-mode-label">{t('accounting.searchMode')}</InputLabel>
                    <Select
                        labelId="accounting-search-mode-label"
                        id="accounting-search-mode"
                        value={searchMode}
                        label={t('accounting.searchMode')}
                        onChange={(event) =>
                            setSearchMode(event.target.value as 'contains' | 'fuzzy' | 'startsWith')
                        }
                    >
                        <MenuItem value="contains">Exact (contains)</MenuItem>
                        <MenuItem value="fuzzy">Fuzzy</MenuItem>
                        <MenuItem value="startsWith">Starts with</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        ),
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
                <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenModal(row.original)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                    <IconButton color="error" onClick={() => handleDelete(row.original)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        rowCount: meta?.total ?? 0,
        state: {
            columnFilters,
            columnVisibility,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            sorting,
        },
    });

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {t('accounting.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" onClick={() => handleGeneratePdf()}>
                        {t('accounting.generatePdf')}
                    </Button>
                    <Button variant="outlined" onClick={() => handleOpenModal(undefined, 'document_only')}>
                        {t('accounting.createDocument')}
                    </Button>
                    <Button variant="contained" onClick={() => handleOpenModal(undefined, 'booking')}>
                        {t('accounting.createBooking')}
                    </Button>
                </Box>
            </Box>

            <TextField
                fullWidth
                label={t('common.search', { defaultValue: 'Search' })}
                placeholder={t('accounting.searchAllColumns')}
                value={globalFilter ?? ''}
                onChange={(event) => setGlobalFilter(event.target.value)}
                inputRef={searchInputRef}
                sx={{ mb: 2, maxWidth: 640 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                        <InputAdornment position="end">
                            <Tooltip title={t('common.clear', { defaultValue: 'Clear' })}>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                    }}
                                    onClick={() => {
                                        setGlobalFilter('');
                                        requestAnimationFrame(() => {
                                            searchInputRef.current?.focus();
                                        });
                                    }}
                                    aria-label="Clear search"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ) : undefined,
                }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {t('accounting.quickFilters')}
                </Typography>
                <Chip
                    label={t('accounting.filterLastYear', { year: new Date().getFullYear() - 1 })}
                    size="small"
                    clickable
                    color={quickFilters.lastYear ? 'primary' : 'default'}
                    variant={quickFilters.lastYear ? 'filled' : 'outlined'}
                    onClick={() =>
                        setQuickFilters((prev) => ({ ...prev, lastYear: !prev.lastYear }))
                    }
                />
                <Chip
                    label={t('accounting.filterDocumentsOnly')}
                    size="small"
                    clickable
                    color={quickFilters.documentsOnly ? 'primary' : 'default'}
                    variant={quickFilters.documentsOnly ? 'filled' : 'outlined'}
                    onClick={() =>
                        setQuickFilters((prev) => ({ ...prev, documentsOnly: !prev.documentsOnly }))
                    }
                />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={isExportingZip ? <CircularProgress size={16} /> : <FolderZipIcon />}
                    disabled={isExportingZip}
                    onClick={() => void handleExportAttachmentsZip()}
                    sx={{ ml: { xs: 0, sm: 'auto' } }}
                >
                    {t('accounting.downloadAttachments')}
                </Button>
            </Box>

            <MaterialReactTable table={table} />

            <Dialog
                open={isModalOpen}
                onClose={() => {
                    void requestCloseModal();
                }}
                maxWidth={false}
                fullWidth
                disableEscapeKeyDown={autoSaveStatus === 'saving' || isClosingModal}
                slotProps={{
                    paper: {
                        onDragEnter: handleAttachmentDragEnter,
                        onDragLeave: handleAttachmentDragLeave,
                        onDragOver: handleAttachmentDragOver,
                        onDrop: (e: React.DragEvent) => void handleAttachmentDrop(e),
                        sx: {
                            position: 'relative',
                            width: '80vw',
                            maxWidth: '80vw',
                            ...(isAttachmentDropOver && {
                                outline: '2px dashed',
                                outlineColor: 'primary.main',
                                outlineOffset: -6,
                            }),
                        },
                    },
                }}
            >
                <DialogTitle>
                    {selected
                        ? t('accounting.editTitle')
                        : isDocumentOnly
                            ? t('accounting.createDocument')
                            : t('accounting.createBooking')}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            mt: 1,
                            alignItems: 'stretch',
                        }}
                    >
                        <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                            <Box sx={{ mb: 2 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={formData.kind}
                                    onChange={(_e, value: AccountingKind | null) => {
                                        if (!value) {
                                            return;
                                        }
                                        setFormData((prev) => ({
                                            ...prev,
                                            kind: value,
                                            ...(value === 'document_only'
                                                ? {
                                                      account_debit: '',
                                                      account_credit: '',
                                                      account_debit_description: '',
                                                      account_credit_description: '',
                                                  }
                                                : {}),
                                        }));
                                        setErrors({});
                                    }}
                                >
                                    <ToggleButton value="booking">{t('accounting.kindBooking')}</ToggleButton>
                                    <ToggleButton value="document_only">{t('accounting.kindDocument')}</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    columnGap: 1.5,
                                    rowGap: 1,
                                }}
                            >
                                <Autocomplete
                                    freeSolo
                                    options={[...ACCOUNTING_AUTHOR_PRESETS]}
                                    value={formData.author}
                                    onChange={(_, value) => {
                                        setFormData((prev) => ({ ...prev, author: value ?? '' }));
                                        setErrors((prev) => ({ ...prev, author: '' }));
                                    }}
                                    onInputChange={(_, value) => {
                                        setFormData((prev) => ({ ...prev, author: value }));
                                        setErrors((prev) => ({ ...prev, author: '' }));
                                    }}
                                    renderInput={(params) => (
                                        <MarkerHighlightTextField
                                            {...params}
                                            label={t('accounting.author', { defaultValue: 'Recorded by' })}
                                            error={!!errors.author}
                                            helperText={errors.author}
                                            fullWidth
                                        />
                                    )}
                                />
                                <DatePicker
                                    label={t('accounting.referenceYear', { defaultValue: 'Reference year' })}
                                    views={['year']}
                                    openTo="year"
                                    format="yyyy"
                                    open={referenceYearPickerOpen}
                                    onOpen={() => setReferenceYearPickerOpen(true)}
                                    onClose={() => setReferenceYearPickerOpen(false)}
                                    value={referenceYearToDate(formData.reference_year)}
                                    minDate={new Date(REFERENCE_YEAR_MIN, 0, 1)}
                                    maxDate={new Date(REFERENCE_YEAR_MAX, 11, 31)}
                                    onChange={(newValue) => {
                                        const val = newValue ? String(newValue.getFullYear()) : '';
                                        setFormData((prev) => ({ ...prev, reference_year: val }));
                                        setErrors((prev) => ({
                                            ...prev,
                                            reference_year:
                                                val && !/^(19|20)\d{2}$/.test(val)
                                                    ? t('validation.numberWithLength', { length: 4, example: '2025' })
                                                    : '',
                                        }));
                                        setReferenceYearPickerOpen(false);
                                    }}
                                    slotProps={{
                                        textField: {
                                            error: !!errors.reference_year,
                                            helperText:
                                                errors.reference_year ||
                                                t('accounting.referenceYearHint', {
                                                    defaultValue:
                                                        'Tax or filing year this item relates to (may differ from document date).',
                                                }),
                                            fullWidth: true,
                                            onClick: () => setReferenceYearPickerOpen(true),
                                            inputProps: { readOnly: true },
                                        },
                                    }}
                                />
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <DatePicker
                                        label={t('accounting.date')}
                                        value={formData.date}
                                        onChange={(newValue) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                date: newValue,
                                                reference_year:
                                                    !prev.reference_year.trim() && newValue
                                                        ? String(newValue.getFullYear())
                                                        : prev.reference_year,
                                            }));
                                            setErrors(prev => ({ ...prev, date: newValue ? '' : t('validation.required') }));
                                        }}
                                        slotProps={{
                                            textField: {
                                                error: !!errors.date,
                                                helperText: errors.date,
                                                fullWidth: true,
                                                required: true,
                                                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                                    const parsed = parseFlexibleAccountingDateInput(
                                                        e.clipboardData.getData('text/plain'),
                                                    );
                                                    if (!parsed) {
                                                        return;
                                                    }
                                                    e.preventDefault();
                                                    setFormData((prev) => ({ ...prev, date: parsed }));
                                                    setErrors((prev) => ({ ...prev, date: '' }));
                                                },
                                                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                                    const parsed = parseFlexibleAccountingDateInput(e.target.value);
                                                    if (!parsed) {
                                                        return;
                                                    }
                                                    setFormData((prev) => ({ ...prev, date: parsed }));
                                                    setErrors((prev) => ({ ...prev, date: '' }));
                                                },
                                            },
                                        }}
                                        format={APP_DATE_FORMAT}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                        <MarkerHighlightTextField
                            label={t('accounting.doc')}
                            value={formData.doc}
                            onChange={e => {
                                const val = e.target.value.slice(0, 20);
                                setFormData({ ...formData, doc: val });
                                setErrors(prev => ({
                                    ...prev,
                                    doc: val.length > 20 ? t('validation.stringWithLength', { length: 20 }) : '',
                                }));
                            }}
                            error={!!errors.doc}
                            helperText={errors.doc}
                            fullWidth
                            slotProps={{ htmlInput: { maxLength: 20 }}}
                        />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('accounting.description')}
                                </Typography>
                                <TxtSplitEditor
                                    value={formData.description || ''}
                                    onChange={(value) => {
                                        setFormData((prev) => ({ ...prev, description: value }));
                                        setErrors((prev) => ({ ...prev, description: '' }));
                                    }}
                                    layout="auto"
                                    paneHeight={340}
                                    showHtmlSourceStorageKey={ACCOUNTING_SHOW_HTML_SOURCE_KEY}
                                    showInsertTimestampButton
                                />
                                {errors.description ? (
                                    <Typography color="error" variant="caption" sx={{ mt: 0.75, display: 'block' }}>
                                        {errors.description}
                                    </Typography>
                                ) : null}
                            </Box>

                            <Box sx={{ ...accountingCurrencyFieldsShellSx, mb: 3 }}>
                                <TextField
                                    label={t('accounting.amountCurrency')}
                                    value={formData.amount_currency}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9.]/g, '');
                                        const [integerPart, decimalPart = ''] = val.split('.');
                                        if (integerPart.length <= 8 && (!decimalPart || decimalPart.length <= 2)) {
                                            setFormData({ ...formData, amount_currency: val });
                                            setErrors(prev => ({ ...prev, amount_currency: '' }));
                                        }
                                    }}
                                    error={!!errors.amount_currency}
                                    helperText={errors.amount_currency}
                                    fullWidth
                                />
                                <Autocomplete
                                    options={exchangeCurrencyOptions}
                                    value={
                                        formData.exchange_currency.trim()
                                            ? formData.exchange_currency.trim().toUpperCase()
                                            : undefined
                                    }
                                    onChange={(_, newValue) => {
                                        const v = newValue ?? '';
                                        setFormData({ ...formData, exchange_currency: v });
                                        setErrors(prev => ({
                                            ...prev,
                                            exchange_currency:
                                                v && v.length !== 3
                                                    ? t('validation.stringWithLength', { length: 3, example: '(e.g., USD)' })
                                                    : '',
                                        }));
                                    }}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(a, b) => a === b}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('accounting.exchangeCurrency')}
                                            error={!!errors.exchange_currency}
                                            helperText={errors.exchange_currency}
                                        />
                                    )}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    columnGap: 1.5,
                                    rowGap: 1,
                                }}
                            >
                        {!isDocumentOnly && (
                            <>
                        <TextField
                            label={t('accounting.accountDebit')}
                            value={formData.account_debit}
                            onChange={e => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                setFormData({ ...formData, account_debit: val });
                                setErrors(prev => ({
                                    ...prev,
                                    account_debit:
                                        val && !/^\d{0,10}$/.test(val)
                                            ? t('validation.numberWithLength', { length: 10 })
                                            : '',
                                }));
                            }}
                            error={!!errors.account_debit}
                            helperText={errors.account_debit}
                            fullWidth
                            slotProps={{ htmlInput: { maxLength: 10 }}}
                        />
                        <TextField
                            label={t('accounting.accountCredit')}
                            value={formData.account_credit}
                            onChange={e => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                setFormData({ ...formData, account_credit: val });
                                setErrors(prev => ({
                                    ...prev,
                                    account_credit:
                                        val && !/^\d{0,10}$/.test(val)
                                            ? t('validation.numberWithLength', { length: 10 })
                                            : '',
                                }));
                            }}
                            error={!!errors.account_credit}
                            helperText={errors.account_credit}
                            fullWidth
                            slotProps={{ htmlInput: { maxLength: 10 }}}
                        />
                        <MarkerHighlightTextField
                            label={t('accounting.accountDebitDescription')}
                            value={formData.account_debit_description}
                            onChange={e => {
                                setFormData({ ...formData, account_debit_description: e.target.value });
                                setErrors(prev => ({ ...prev, account_debit_description: '' }));
                            }}
                            error={!!errors.account_debit_description}
                            helperText={errors.account_debit_description}
                            fullWidth
                        />
                        <MarkerHighlightTextField
                            label={t('accounting.accountCreditDescription')}
                            value={formData.account_credit_description}
                            onChange={e => {
                                setFormData({ ...formData, account_credit_description: e.target.value });
                                setErrors(prev => ({ ...prev, account_credit_description: '' }));
                            }}
                            error={!!errors.account_credit_description}
                            helperText={errors.account_credit_description}
                            fullWidth
                        />
                            </>
                        )}
                        <TextField
                            label={t('accounting.exchangeRate')}
                            value={formData.exchange_rate}
                            onChange={e => {
                                const val = e.target.value.replace(/[^0-9.]/g, '');
                                const [integerPart, decimalPart = ''] = val.split('.');
                                if (integerPart.length <= 4 && (!decimalPart || decimalPart.length <= 6)) {
                                    setFormData({ ...formData, exchange_rate: val });
                                    if (!val.trim()) {
                                        setErrors(prev => ({ ...prev, exchange_rate: '' }));
                                        return;
                                    }
                                    const normalized = ensureDecimalPlaces(val, 6);
                                    setErrors(prev => ({
                                        ...prev,
                                        exchange_rate:
                                            !/^\d{0,4}(\.\d{6})?$/.test(normalized) || normalized.replace('.', '').length > 10
                                                ? t('validation.decimalWithLength', { length: 6, example: '12.345678' })
                                                : '',
                                    }));
                                }
                            }}
                            error={!!errors.exchange_rate}
                            helperText={errors.exchange_rate}
                            fullWidth
                        />
                        <TextField
                            label={t('accounting.amount')}
                            value={formData.amount}
                            onChange={e => {
                                const val = e.target.value.replace(/[^0-9.]/g, '');
                                const [integerPart, decimalPart = ''] = val.split('.');
                                if (integerPart.length <= 8 && (!decimalPart || decimalPart.length <= 2)) {
                                    setFormData({ ...formData, amount: val });
                                    if (!val.trim()) {
                                        setErrors(prev => ({ ...prev, amount: '' }));
                                        return;
                                    }
                                    const normalized = ensureDecimalPlaces(val, 2);
                                    setErrors(prev => ({
                                        ...prev,
                                        amount:
                                            !/^\d{0,8}(\.\d{2})?$/.test(normalized) || normalized.replace('.', '').length > 10
                                                ? t('validation.decimalWithLength', { length: 2, example: '12345.67' })
                                                : '',
                                    }));
                                }
                            }}
                            error={!!errors.amount}
                            helperText={errors.amount}
                            fullWidth
                        />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flex: { xs: '1 1 auto', md: '0 0 50%' },
                                width: { xs: '100%', md: 'auto' },
                                minWidth: { md: 0 },
                                flexShrink: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                minHeight: 0,
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={handleAttachmentsSelected}
                            />
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="subtitle2">
                                    {t('accounting.filePreview', { defaultValue: 'File preview' })}
                                </Typography>
                                <Tooltip
                                    title={t('accounting.previewLargeModal', { defaultValue: 'Large preview' })}
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="fullscreen preview"
                                            disabled={!canFullscreenPreview}
                                            onClick={() => setIsPreviewFullscreenOpen(true)}
                                        >
                                            <FullscreenIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                            <Box
                                sx={{
                                    position: 'relative',
                                    flex: 1,
                                    minHeight: 360,
                                    maxHeight: { xs: 420, md: 'min(68vh, 640px)' },
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {!previewSrc && !previewBlobLoading && (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                                        {t('accounting.previewEmpty', {
                                            defaultValue:
                                                'Pick an attachment below or add files — PDF and images preview here.',
                                        })}
                                    </Typography>
                                )}
                                {isPreviewPdf && previewSrc && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            minHeight: 260,
                                            alignSelf: 'stretch',
                                            overflow: 'auto',
                                            bgcolor: 'grey.100',
                                        }}
                                    >
                                        <AccountingPdfJsPreview fileUrl={previewSrc} />
                                    </Box>
                                )}
                                {isPreviewImage && previewSrc && (
                                    <img
                                        alt=""
                                        src={previewSrc}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                )}
                                {previewSrc &&
                                    !isPreviewPdf &&
                                    !isPreviewImage && (
                                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                                            {t('accounting.previewUnsupported', {
                                                defaultValue: 'No in-app preview for this type — use Download.',
                                            })}
                                        </Typography>
                                    )}
                                {previewBlobLoading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            zIndex: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                                            backdropFilter: 'blur(4px)',
                                        }}
                                    >
                                        <CircularProgress size={40} thickness={4} />
                                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
                                            {t('accounting.previewLoading', {
                                                defaultValue: 'Loading preview…',
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Stack spacing={1}>
                                <Typography variant="subtitle2">
                                    {t('accounting.attachments', { defaultValue: 'Attachments' })}
                                </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('accounting.attachmentsHint', {
                                    defaultValue:
                                        'Drag files anywhere on this dialog, use the zone below, or Add files. Stored under accountings/ on the default disk.',
                                })}
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: isAttachmentDropOver ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    py: 3,
                                    px: 2,
                                    textAlign: 'center',
                                    bgcolor: isAttachmentDropOver ? 'action.hover' : 'action.selected',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(ke) => {
                                    if (ke.key === 'Enter' || ke.key === ' ') {
                                        ke.preventDefault();
                                        fileInputRef.current?.click();
                                    }
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {t('accounting.dropFilesHere', {
                                        defaultValue: 'Click to browse, or drop files anywhere on this dialog',
                                    })}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fileInputRef.current?.click();
                                    }}
                                >
                                    {t('accounting.addFiles', { defaultValue: 'Add files' })}
                                </Button>
                                {formData.attachments.map((a) => (
                                    <Stack key={a.stored_path} direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '100%' }}>
                                        <Chip
                                            label={a.original_name}
                                            onDelete={() => void removeAttachment(a.stored_path)}
                                            onClick={() => {
                                                setListPreviewAttachments(null);
                                                setPreviewPath(a.stored_path);
                                            }}
                                            color={previewPath === a.stored_path ? 'primary' : 'default'}
                                            variant={previewPath === a.stored_path ? 'filled' : 'outlined'}
                                            size="small"
                                            sx={{ cursor: 'pointer', maxWidth: { xs: 200, sm: 240 } }}
                                        />
                                        <Tooltip title={t('common.download', { defaultValue: 'Download' })}>
                                            <IconButton
                                                size="small"
                                                aria-label="download"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    void accountingService.downloadAttachment(a.stored_path, a.original_name);
                                                }}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                ))}
                            </Stack>
                            </Stack>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            mr: 'auto',
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 1,
                            fontWeight: 600,
                            color: autoSaveStatus === 'error' ? 'error.dark' : 'text.primary',
                            bgcolor:
                                autoSaveStatus === 'pending'
                                    ? 'warning.light'
                                    : autoSaveStatus === 'saving'
                                        ? 'info.light'
                                        : autoSaveStatus === 'saved'
                                            ? 'success.light'
                                            : autoSaveStatus === 'error'
                                                ? 'error.light'
                                                : 'action.hover',
                        }}
                    >
                        {autoSaveStatus === 'pending' && t('accounting.autosavePending', { defaultValue: 'Autosave pending…' })}
                        {autoSaveStatus === 'saving' && t('accounting.autosaveSaving', { defaultValue: 'Autosaving…' })}
                        {autoSaveStatus === 'saved' && t('accounting.autosaveSaved', { defaultValue: 'Saved ✓' })}
                        {autoSaveStatus === 'error' && t('accounting.autosaveError', { defaultValue: 'Autosave failed' })}
                        {autoSaveStatus === 'idle' && t('accounting.autosaveIdle', { defaultValue: 'Autosave ready' })}
                    </Typography>
                    <Button onClick={closeModalImmediately}>{t('common.cancel')}</Button>
                    <Button onClick={() => void handleSubmit()} variant="contained" disabled={createMutation.isPending || updateMutation.isPending}>
                        {selected ? t('common.save') : t('common.create')}
                    </Button>
                </DialogActions>
                {(createMutation.isPending || updateMutation.isPending) && <LinearProgress />}
            </Dialog>
            <Dialog
                open={isPreviewFullscreenOpen}
                onClose={closeAttachmentPreviewFullscreen}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '90vw',
                        maxWidth: '90vw',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="h6" component="span">
                            {t('accounting.filePreview', { defaultValue: 'File preview' })}
                        </Typography>
                        {largePreviewFileName ? (
                            <Typography variant="body2" color="text.secondary" noWrap title={largePreviewFileName}>
                                {largePreviewFileName}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', minHeight: { xs: '75vh', sm: '82vh' } }}>
                        {isPreviewPdf && previewSrc && (
                            <Box
                                sx={{
                                    height: { xs: '75vh', sm: '82vh' },
                                    overflow: 'auto',
                                    bgcolor: 'grey.100',
                                }}
                            >
                                <AccountingPdfJsPreview fileUrl={previewSrc} />
                            </Box>
                        )}
                        {isPreviewImage && previewSrc && (
                            <Box
                                sx={{
                                    height: { xs: '75vh', sm: '82vh' },
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    p: 1,
                                }}
                            >
                                <img
                                    alt=""
                                    key={previewPath ?? 'modal-img'}
                                    src={previewSrc}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        )}
                        {previewSrc &&
                            !isPreviewPdf &&
                            !isPreviewImage && (
                                <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('accounting.previewUnsupported', {
                                            defaultValue: 'No in-app preview for this type — use Download.',
                                        })}
                                    </Typography>
                                </Box>
                            )}
                        {previewBlobLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                                    backdropFilter: 'blur(4px)',
                                }}
                            >
                                <CircularProgress size={48} thickness={4} />
                                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
                                    {t('accounting.previewLoading', {
                                        defaultValue: 'Loading preview…',
                                    })}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
                    {previewAttachments.length > 1 && largePreviewAttachmentIndex >= 0 ? (
                        <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={goLargePreviewPrev}
                                disabled={largePreviewAttachmentIndex <= 0}
                            >
                                {t('accounting.previewPrev', { defaultValue: 'Prev' })}
                            </Button>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {largePreviewAttachmentIndex + 1}/{previewAttachments.length}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={goLargePreviewNext}
                                disabled={largePreviewAttachmentIndex >= previewAttachments.length - 1}
                            >
                                {t('accounting.previewNext', { defaultValue: 'Next' })}
                            </Button>
                        </Box>
                    ) : null}
                    {previewPath && largePreviewFileName ? (
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                void accountingService.downloadAttachment(previewPath, largePreviewFileName);
                            }}
                        >
                            {t('common.download', { defaultValue: 'Download' })}
                        </Button>
                    ) : null}
                    <Button onClick={closeAttachmentPreviewFullscreen}>{t('common.close', { defaultValue: 'Close' })}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle>{t('accounting.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('accounting.deleteConfirmation')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
